import moment, { Moment } from "moment";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import styled from "styled-components";
import { frontEndConfig } from "../../config/config";
import { routeConfig } from "../../config/language/ja-JP/presentation/route_text";
import {
  AppNotificationMessage,
  AppNotificationType,
} from "../../domain/model/app_notification_message";
import { FrontDate } from "../../domain/model/frontDate";
import { Project } from "../../domain/model/project";
import { IContractRepository } from "../../domain/repository/contract_repository";
import { IProjectRepository } from "../../domain/repository/project_repository";
import { IUserRepository } from "../../domain/repository/user_repository";
import {
  IFileUseCase,
  createFileUsecase,
} from "../../domain/usecase/file_usecase";
import { IApiClient, createApiClient } from "../../infra/api/client";
import { entity } from "../../infra/api/rpc/api";
import { createContractAPI } from "../../infra/api/service/contract_api";
import { createProjectAPI } from "../../infra/api/service/project_api";
import { createUserAPI } from "../../infra/api/service/user_api";
import BtnCorporateLarge from "../common/atom/btn/btn_corporate_large";
import BtnCorporateMiddle from "../common/atom/btn/btn_corporate_middle";
import ParagraphSup from "../common/atom/text/paragraph_sup";
import PageWrapper from "../common/atom/wrapper/page_wrapper";
import SubmitButtonWrapper from "../common/atom/wrapper/submit_btn_wrapper/submit_btn_wrapper";
import Breadcrumbs from "../common/molecular/breadcrumbs/breadcrumbs";
import AppNotificationWithTexts from "../common/organism/app_notification_with_texts";
import Progress from "../common/organism/progress";
import ProjectThumbnail from "../component/client/contract/share/project_thumbnail";
import { TBreadcrumbItem } from "../type/common";

const uploadFileMaxSize = frontEndConfig.uploadFileMaxSize;

const apiClient: IApiClient = createApiClient();
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const contractRepository: IContractRepository = createContractAPI(apiClient);
const userRepository: IUserRepository = createUserAPI(apiClient);
const fileUseCase: IFileUseCase = createFileUsecase();

interface IProps
  extends RouteComponentProps<{
    companyId: string;
    projectId: string;
    customerId: string;
  }> {}

const CustomerContractRegistration: React.FC<IProps> = (props) => {
  const now: Moment = moment();
  const after1year: Moment = moment().add(1, "years");

  const { history } = props;

  const [project, setProject] = React.useState<Project | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [title, setTitle] = React.useState<string>("");
  const [description, setDescription] = React.useState<string>("");
  const [meetingNum, setMeetingNum] = React.useState<number>(1);
  const [meetingInterval, setMeetingInteral] = React.useState<number>(1);
  const [meetingMinutes, setMeetingMinutes] = React.useState<number>(60);
  const [workingHour, setWorkingHour] = React.useState<number>(10);
  const [workingInterval, setWorkingInterval] = React.useState<number>(1);
  const [feePerMonth, setFeePerMonth] = React.useState<number>(500000);
  const [contractType, setContractType] = React.useState<number>(1);
  const [startFromYear, setStartFromYear] = React.useState<number>(
    parseInt(now.year().toString(), undefined)
  );
  const [startFromMonth, setStartFromMonth] = React.useState<number>(
    parseInt(now.month().toString(), undefined) + 1
  );
  const [endToYear, setEndToYear] = React.useState<number>(
    parseInt(after1year.year().toString(), undefined)
  );
  const [endToMonth, setEndToMonth] = React.useState<number>(
    parseInt(after1year.month().toString(), undefined) + 1
  );
  const [file, setFile] = React.useState<File | null>(null);
  const [messages, setMessages] = React.useState<AppNotificationMessage[]>([]);

  const companyId = props.match.params.companyId;
  const projectId = props.match.params.projectId;
  const customerId = props.match.params.customerId;

  React.useEffect(() => {
    try {
      projectRepository.get(projectId).then((p) => {
        setProject(p);
      });
    } catch (e) {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  }, []);

  // 初期化
  if (project == null) {
    return <Progress canAnimation={false} />;
  }

  const breadcrumbItems: TBreadcrumbItem[] = [
    routeConfig.clientRoot,
    { text: `[成約報告]${project.name}` },
  ];

  const handleOnFileChange = (e: any) => {
    const target: HTMLInputElement = e.target as HTMLInputElement;
    const _file: File | null = target.files ? target.files.item(0) : null;

    if (_file) {
      if (_file.size > uploadFileMaxSize) {
        alert("ファイルサイズが大きすぎます");
      } else {
        setFile(_file);
      }
    }
  };

  const save = async () => {
    if (title === "") {
      alert("タイトルは必須です");
      return;
    }
    if (description === "") {
      alert("業務内容は必須です");
      return;
    }
    if (startFromYear === 0) {
      alert("契約開始時期は必須です");
      return;
    }
    if (startFromMonth === 0) {
      alert("契約開始時期は必須です");
      return;
    }
    if (endToYear === 0) {
      alert("契約終了時期は必須です");
      return;
    }
    if (endToMonth === 0) {
      alert("契約終了時期は必須です");
      return;
    }
    if (feePerMonth === 0) {
      alert("契約の報酬は必須です");
      return;
    }
    if (contractType === 0) {
      alert("契約の種類は必須です");
      return;
    }
    const meetingInvalid =
      meetingNum === 0 || meetingInterval === 0 || meetingMinutes === 0;
    const workingInvalid = workingHour === 0 || workingInterval === 0;
    if (meetingInvalid && workingInvalid) {
      alert("打ち合わせについて or 実稼働については必須です");
      return;
    }

    setIsLoading(true);

    try {
      const user = await userRepository.getMe();
      let fileUrl = "";
      if (file) {
        fileUrl = await fileUseCase.createContractFileForCustomer(
          user,
          file!,
          companyId,
          projectId
        );
      }

      await contractRepository.create(
        projectId,
        customerId,
        title,
        description,
        meetingNum,
        meetingInterval,
        meetingMinutes,
        workingHour,
        workingInterval,
        feePerMonth,
        contractType,
        `${startFromYear}-${startFromMonth}`,
        `${endToYear}-${endToMonth}`,
        fileUrl
      );

      setIsLoading(false);
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.INFO,
          "契約を作成しました",
          FrontDate.fromEmpty()
        ),
      ]);

      history.push("/customer/entries?p=1");
    } catch (e) {
      setIsLoading(false);
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  };

  return (
    <React.Fragment>
      {messages.length > 0 && (
        <AppNotificationWithTexts
          onClose={(id: string) =>
            (event: any): void => {
              setMessages([]);
            }}
          appNotificationMessages={messages}
        />
      )}

      {isLoading && <Progress canAnimation={false} />}
      <PageWrapper>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />
        <ProjectThumbnail project={project} />
        <FormWrapper>
          <FormDescription>
            成約報告に必要な以下の情報を入力してください。
          </FormDescription>
          <InputSection>
            契約のタイトル<span style={{ color: "red" }}>*</span>
          </InputSection>
          <Input
            placeholder="例）新規サービスのグロースハックPM案件とPM組織の強化"
            fromRight={false}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />

          <div
            style={{ display: "flex", alignItems: "center", marginTop: "24px" }}
          >
            <div style={{ width: "300px" }}>
              <InputSection>
                契約開始時期<span style={{ color: "red" }}>*</span>
              </InputSection>
              <div style={{ display: "flex", alignItems: "end" }}>
                <div style={{ width: "100px", marginRight: "10px" }}>
                  <Input
                    placeholder="2022"
                    fromRight={true}
                    value={startFromYear}
                    onChange={(event) =>
                      setStartFromYear(
                        parseInt(event.target.value || "0", undefined)
                      )
                    }
                  />
                </div>
                <div style={{ fontSize: "12px" }}>年</div>
                <div
                  style={{
                    width: "100px",
                    marginRight: "10px",
                    marginLeft: "32px",
                  }}
                >
                  <Input
                    placeholder="04"
                    fromRight={true}
                    value={startFromMonth}
                    onChange={(event) =>
                      setStartFromMonth(
                        parseInt(event.target.value || "0", undefined)
                      )
                    }
                  />
                </div>
                <div style={{ fontSize: "12px" }}>月</div>
              </div>
            </div>

            <div style={{ paddingTop: "20px" }}>〜</div>

            <div style={{ width: "300px", marginLeft: "40px" }}>
              <InputSection>
                契約終了時期<span style={{ color: "red" }}>*</span>
              </InputSection>
              <div style={{ display: "flex", alignItems: "end" }}>
                <div style={{ width: "100px", marginRight: "10px" }}>
                  <Input
                    placeholder="2022"
                    fromRight={true}
                    value={endToYear}
                    onChange={(event) =>
                      setEndToYear(
                        parseInt(event.target.value || "0", undefined)
                      )
                    }
                  />
                </div>
                <div style={{ fontSize: "12px" }}>年</div>
                <div
                  style={{
                    width: "100px",
                    marginRight: "10px",
                    marginLeft: "32px",
                  }}
                >
                  <Input
                    placeholder="04"
                    fromRight={true}
                    value={endToMonth}
                    onChange={(event) =>
                      setEndToMonth(
                        parseInt(event.target.value || "0", undefined)
                      )
                    }
                  />
                </div>
                <div style={{ fontSize: "12px" }}>月</div>
              </div>
            </div>
          </div>

          <div style={{ width: "100%", marginTop: "24px" }}>
            <InputSection>
              打ち合わせについて<span style={{ color: "red" }}>*</span>
            </InputSection>
            <div style={{ display: "flex", alignItems: "end" }}>
              <div style={{ fontSize: "12px", marginRight: "10px" }}>約</div>
              <div style={{ width: "100px", marginRight: "10px" }}>
                <Input
                  placeholder="4"
                  fromRight={true}
                  value={meetingNum}
                  onChange={(event) =>
                    setMeetingNum(
                      parseInt(event.target.value || "0", undefined)
                    )
                  }
                />
              </div>
              <div style={{ fontSize: "12px" }}>回/</div>
              <div
                style={{
                  width: "100px",
                  marginRight: "10px",
                  marginLeft: "10px",
                }}
              >
                <Select
                  value={meetingInterval}
                  onChange={(event) =>
                    setMeetingInteral(
                      parseInt(event.target.value || "0", undefined)
                    )
                  }
                >
                  <option value={entity.Contract.Interval.Interval_Unknown}>
                    ~~
                  </option>
                  <option value={entity.Contract.Interval.Interval_Month}>
                    月
                  </option>
                  <option value={entity.Contract.Interval.Interval_Week}>
                    週
                  </option>
                </Select>
              </div>
              <div style={{ fontSize: "12px" }}>の頻度で、1回あたり約</div>
              <div
                style={{
                  width: "100px",
                  marginRight: "10px",
                  marginLeft: "10px",
                }}
              >
                <Input
                  placeholder="60"
                  fromRight={true}
                  value={meetingMinutes}
                  onChange={(event) =>
                    setMeetingMinutes(
                      parseInt(event.target.value || "0", undefined)
                    )
                  }
                />
              </div>
              <div style={{ fontSize: "12px" }}>分の打ち合わせを想定</div>
            </div>
          </div>

          <div style={{ width: "100%", marginTop: "24px" }}>
            <InputSection>
              実稼働について{" "}
              <span style={{ color: "gray" }}>※打ち合わせを除いた稼働時間</span>
            </InputSection>
            <div style={{ display: "flex", alignItems: "end" }}>
              <div style={{ fontSize: "12px", marginRight: "10px" }}>約</div>
              <div style={{ width: "100px", marginRight: "10px" }}>
                <Input
                  placeholder="60"
                  fromRight={true}
                  value={workingHour}
                  onChange={(event) =>
                    setWorkingHour(
                      parseInt(event.target.value || "0", undefined)
                    )
                  }
                />
              </div>
              <div style={{ fontSize: "12px" }}>時間/</div>
              <div
                style={{
                  width: "100px",
                  marginRight: "10px",
                  marginLeft: "10px",
                }}
              >
                <Select
                  value={workingInterval}
                  onChange={(event) =>
                    setWorkingInterval(
                      parseInt(event.target.value || "0", undefined)
                    )
                  }
                >
                  <option value={entity.Contract.Interval.Interval_Unknown}>
                    ~~
                  </option>
                  <option value={entity.Contract.Interval.Interval_Month}>
                    月
                  </option>
                  <option value={entity.Contract.Interval.Interval_Week}>
                    週
                  </option>
                </Select>
              </div>
              <div style={{ fontSize: "12px" }}>の実稼働を想定</div>
            </div>
          </div>

          <div
            style={{ display: "flex", alignItems: "center", marginTop: "24px" }}
          >
            <div style={{ width: "170px" }}>
              <InputSection>
                契約の報酬<span style={{ color: "red" }}>*</span>
              </InputSection>
              <div style={{ display: "flex", alignItems: "end" }}>
                <div style={{ width: "100px", marginRight: "10px" }}>
                  <Input
                    placeholder="60"
                    fromRight={true}
                    value={feePerMonth}
                    onChange={(event) =>
                      setFeePerMonth(
                        parseInt(event.target.value || "0", undefined)
                      )
                    }
                  />
                </div>
                <div style={{ fontSize: "12px" }}>円/月</div>
              </div>
            </div>

            <div style={{ width: "170px", marginLeft: "30px" }}>
              <InputSection>
                契約の種類<span style={{ color: "red" }}>*</span>
              </InputSection>
              <div style={{ display: "flex", alignItems: "end" }}>
                <div style={{ width: "100px", marginRight: "10px" }}>
                  <Select
                    value={contractType}
                    onChange={(event) =>
                      setContractType(
                        parseInt(event.target.value || "0", undefined)
                      )
                    }
                  >
                    <option value={entity.Contract.Type.Type_Unknown}>
                      ~~
                    </option>
                    <option value={entity.Contract.Type.Type_Delegation}>
                      委任
                    </option>
                    <option value={entity.Contract.Type.Type_Contract}>
                      受託
                    </option>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <InputSection>
            業務内容<span style={{ color: "red" }}>*</span>
          </InputSection>
          <TextArea
            placeholder="例 )2021年の春にリリースした新製品、〇〇のグロースハックを目的に、プロダクトマネジメント業務を依頼。オンボーディングからアクティベーションプロセスの改善設計を中心に、IT部門と連携し、改善作業を行う。また社内でのプロダクトチームの組織体制を見直してもらう。"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </FormWrapper>

        <InputWrapper>
          {file && <ParagraphSup>UploadFile有り</ParagraphSup>}

          <input
            accept="application/pdf,
                    application/vnd.ms-powerpoint,
                    application/vnd.openxmlformats-officedocument.presentationml.presentation,
                    application/msword,
                    application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                    application/vnd.ms-excel,
                    application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            id="contained-button-file"
            multiple={false}
            type="file"
            onChange={handleOnFileChange}
          />
          <label htmlFor="contained-button-file">
            <BtnCorporateMiddle as={"span"} widthSize={"200px"}>
              契約書をアップロード
            </BtnCorporateMiddle>
          </label>
        </InputWrapper>

        <InputTextWrapper>
          <InputCaution>※契約書のアップロードは任意です</InputCaution>
        </InputTextWrapper>

        <SubmitButtonWrapper>
          <BtnCorporateLarge
            name="ClientContractCreateForm-submit"
            type="submit"
            widthSize="200px"
            onClick={() => {
              save();
            }}
          >
            送信する
          </BtnCorporateLarge>
        </SubmitButtonWrapper>
      </PageWrapper>
    </React.Fragment>
  );
};

export default CustomerContractRegistration;

const FormWrapper = styled.div`
  width: 100%;
  margin-bottom: 40px;
  padding: 30px 20px;
  background-color: rgba(241, 246, 248, 0.5);
`;

const FormDescription = styled.div`
  margin-bottom: 16px;
  font-size: 12px;
`;

const InputSection = styled.div`
  margin-top: 20px;
  font-size: 12px;
`;

const Input = styled.input<{ fromRight: boolean }>`
  font-size: 12px;
  margin-top: 8px;
  height: 50px;
  border: none;
  border: solid 1px #a4a4a4;
  border-radius: 3px;
  width: 100%;
  padding: 0px 5px;
  text-align: ${({ fromRight }) => (fromRight ? "right" : "left")};
`;

const Select = styled.select`
  font-size: 12px;
  margin-top: 8px;
  height: 50px;
  border: none;
  border: solid 1px #a4a4a4;
  border-radius: 3px;
  width: 100%;
  padding: 0px 5px;
`;

const TextArea = styled.textarea`
  font-size: 12px;
  margin-top: 8px;
  height: 50px;
  border: none;
  border: solid 1px #a4a4a4;
  border-radius: 3px;
  width: 100%;
  height: 150px;
  padding: 5px;
`;

const InputWrapper = styled.div`
  text-align: center;
  & > input {
    display: none;
  }
  margin-bottom: 10px;
`;

const InputTextWrapper = styled.div`
  margin-bottom: 50px;
  text-align: center;
`;

const InputCaution = styled.div`
  font-size: 12px;
  color: #a4a4a4;
`;



// WEBPACK FOOTER //
// ./src/presentation/container/customer_contract_registration.tsx